import type { CmsSignpost, FavoriteCode } from '@gov-nx/api';
import { useCmsEvents } from '@gov-nx/core/events';
import type { Optional } from '@gov-nx/core/types';
import { compareProperty, isNumber } from '@gov-nx/core/types';
import { getPageUrlByCode } from '@gov-nx/module/page';
import { getServiceUrlByCode } from '@gov-nx/module/service';
import { compare } from '@gov-nx/utils/common';

const linkResolver = (signpost: CmsSignpost): string => {
	if (signpost.pageCode) {
		return getPageUrlByCode(signpost.pageCode) ?? '#';
	}

	if (signpost.serviceCode) {
		return getServiceUrlByCode(signpost.serviceCode) ?? '#';
	}

	return '#';
};

interface ButtonAction {
	isLink: false;
	onClick: () => void;
}

interface LinkAction {
	isLink: true;
	to: string;
}

export type CMSAction = LinkAction | ButtonAction;

export const useCMSActionResolver = () => {
	const { cmsExternalServiceModal } = useCmsEvents();

	const getAction = (signpost: CmsSignpost): Optional<CMSAction> => {
		if (signpost.pageCode || signpost.serviceCode) {
			return { isLink: true, to: linkResolver(signpost) };
		}

		if (isNumber(signpost.externalServiceId)) {
			return {
				isLink: false,
				onClick: () =>
					cmsExternalServiceModal({
						id: signpost.externalServiceId as number,
					}),
			};
		}

		return;
	};

	const withAction = (signpost: CmsSignpost) => {
		const action = getAction(signpost);
		const code =
			signpost.pageCode || signpost.serviceCode || signpost.externalServiceCode;

		return action ? { ...signpost, action, code } : undefined;
	};

	return {
		withAction,
	};
};

export const isFavorite =
	(favorites: string[]) =>
	({ pageCode, serviceCode, externalServiceCode }: CmsSignpost) =>
		[pageCode, serviceCode, externalServiceCode].some(
			(code): code is FavoriteCode => !!code && favorites.includes(code)
		);

export const signpostComparator =
	(hasSearch: boolean, sortAlphabetically: boolean) =>
	(a: CmsSignpost, b: CmsSignpost) => {
		if (hasSearch) return 0;
		if (sortAlphabetically) return compare(a.name, b.name);
		return compareProperty('order')(a, b);
	};

export const sanitizeSearch = (value: string): string =>
	value
		.replace(/[^\p{L}\p{N},.?! ]/gu, '')
		.replace(/\s+/g, ' ')
		.trim();
